import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2, Save, X, FileText, Calendar } from 'lucide-react';

const CATEGORIES = [
  { key: 'appraisal', label: 'Appraisal Condition', hint: 'Narrative blocks used in the Appraisal Condition / quote summary.' },
  { key: 'roof', label: 'Roof Condition', hint: 'Pre-written text describing general roof condition findings.' },
  { key: 'roof_requirements', label: 'Roof Requirements', hint: 'Remedial roof requirements / recommendation blocks.' },
  { key: 'extra_work', label: 'Extra Work', hint: 'Add-on extra work quotes referenced from an appraisal.' },
];

const BLANK = { name: '', comment: '', recommendation: '', roof_type: '', order: 0, category: 'appraisal', estimate_amount: 0, effective_date: new Date().toISOString().slice(0, 10) };

export default function AppraisalConditionsPage() {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get('category');
  const initialCategory = CATEGORIES.some(c => c.key === urlCategory) ? urlCategory : 'appraisal';
  const [category, setCategory] = useState(initialCategory);

  // Sync category → URL param so shared links work
  useEffect(() => {
    if (urlCategory !== category) {
      setSearchParams(category === 'appraisal' ? {} : { category });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // React to URL param changes (e.g. clicking a different sidebar link)
  useEffect(() => {
    if (urlCategory && CATEGORIES.some(c => c.key === urlCategory) && urlCategory !== category) {
      setCategory(urlCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCategory]);
  const [entries, setEntries] = useState([]);
  const [roofTypes, setRoofTypes] = useState([]);
  const [filterRoofType, setFilterRoofType] = useState('');
  const [editing, setEditing] = useState(null); // entry being edited (full object) or null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [listRes, ddRes] = await Promise.all([
        api.get(`/appraisal-conditions?category=${category}`),
        api.get('/dropdowns'),
      ]);
      setEntries(listRes.data || []);
      setRoofTypes(ddRes.data?.roof_types || []);
    } catch (e) { console.error(e); }
  }, [api, category]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...BLANK, category });
  const openEdit = (e) => setEditing({ ...e, effective_date: e.effective_date || '' });
  const closeModal = () => setEditing(null);

  const save = async () => {
    if (!editing.name?.trim()) return;
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload._id) {
        await api.put(`/appraisal-conditions/${payload._id}`, payload);
      } else {
        await api.post('/appraisal-conditions', payload);
      }
      closeModal();
      load();
    } catch (e) { console.error(e); alert('Save failed'); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this condition entry?')) return;
    try {
      await api.delete(`/appraisal-conditions/${id}`);
      load();
    } catch (e) { console.error(e); }
  };

  const filtered = filterRoofType ? entries.filter(e => (e.roof_type || '').toLowerCase() === filterRoofType.toLowerCase()) : entries;

  const active = CATEGORIES.find(c => c.key === category);

  return (
    <div data-testid="appraisal-conditions-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Appraisal Conditions</h1>
          <p className="text-sm text-slate-500 mt-1">Configure the library of pre-written condition narratives used on appraisals & quotes. Each entry has an effective date so you can track when text was last reviewed.</p>
        </div>
        <button data-testid="new-condition-entry-btn" onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold whitespace-nowrap"><Plus size={14}/> New Entry</button>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-2 border-b border-[#E2E2DF]">
        {CATEGORIES.map(c => (
          <button
            key={c.key}
            data-testid={`ac-cat-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${category === c.key ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            {c.label}
            <span className="ml-2 text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">
              {category === c.key ? filtered.length : ''}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-400 -mt-2">{active?.hint}</p>

      {/* Roof type filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Roof Type:</span>
        <button onClick={() => setFilterRoofType('')} className={`text-xs px-2.5 py-1 rounded-full border ${!filterRoofType ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-500 hover:border-slate-400'}`}>All</button>
        {roofTypes.map(rt => (
          <button key={rt} onClick={() => setFilterRoofType(rt)} className={`text-xs px-2.5 py-1 rounded-full border ${filterRoofType === rt ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-500 hover:border-slate-400'}`}>{rt}</button>
        ))}
      </div>

      {/* Entries table */}
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2 w-16">Order</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 w-28">Roof Type</th>
              <th className="px-3 py-2">Comment (preview)</th>
              {category === 'extra_work' && <th className="px-3 py-2 w-28 text-right">Estimate</th>}
              <th className="px-3 py-2 w-32">Effective Date</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={category === 'extra_work' ? 7 : 6} className="px-4 py-10 text-center text-slate-400">No entries for this category{filterRoofType ? ` and roof type "${filterRoofType}"` : ''}.</td></tr>
            )}
            {filtered.map(e => (
              <tr key={e._id} data-testid={`ac-row-${e._id}`} className="border-t border-[#E2E2DF] hover:bg-slate-50 cursor-pointer" onClick={() => openEdit(e)}>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{e.order}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{e.name}</td>
                <td className="px-3 py-2 text-xs">{e.roof_type ? <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{e.roof_type}</span> : <span className="text-slate-300">-</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-500 max-w-md truncate">{(e.comment || '').slice(0, 140)}{(e.comment || '').length > 140 ? '…' : ''}</td>
                {category === 'extra_work' && <td className="px-3 py-2 text-xs text-right font-mono">{e.estimate_amount > 0 ? `$${Number(e.estimate_amount).toLocaleString()}` : '-'}</td>}
                <td className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1"><Calendar size={11} className="text-slate-300"/>{e.effective_date || '-'}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`ac-delete-${e._id}`} onClick={(ev) => { ev.stopPropagation(); remove(e._id); }} className="text-slate-300 hover:text-red-600 p-1"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#E2E2DF]">
              <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2"><FileText size={18} className="text-[#D35400]"/> {editing._id ? 'Edit' : 'New'} {CATEGORIES.find(c => c.key === editing.category)?.label}</h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Category</label>
                  <select data-testid="ac-edit-category" value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm bg-white">
                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Roof Type</label>
                  <select data-testid="ac-edit-roof-type" value={editing.roof_type || ''} onChange={e => setEditing({...editing, roof_type: e.target.value})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm bg-white">
                    <option value="">All / Any</option>
                    {roofTypes.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Name / Choice</label>
                <input data-testid="ac-edit-name" value={editing.name || ''} onChange={e => setEditing({...editing, name: e.target.value})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm" placeholder="e.g. B&P New Ridges" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Comment / Narrative</label>
                <textarea data-testid="ac-edit-comment" value={editing.comment || ''} onChange={e => setEditing({...editing, comment: e.target.value})} rows={8} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm font-mono" placeholder="Full appraisal condition text..." />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Recommendation</label>
                <textarea data-testid="ac-edit-recommendation" value={editing.recommendation || ''} onChange={e => setEditing({...editing, recommendation: e.target.value})} rows={2} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm" placeholder="Short recommendation / headline." />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Order</label>
                  <input data-testid="ac-edit-order" type="number" value={editing.order || 0} onChange={e => setEditing({...editing, order: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm" />
                </div>
                {editing.category === 'extra_work' && (
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Estimate $</label>
                    <input data-testid="ac-edit-estimate" type="number" step="0.01" value={editing.estimate_amount || 0} onChange={e => setEditing({...editing, estimate_amount: parseFloat(e.target.value) || 0})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm" />
                  </div>
                )}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Effective Date</label>
                  <input data-testid="ac-edit-date" type="date" value={editing.effective_date || ''} onChange={e => setEditing({...editing, effective_date: e.target.value})} className="w-full mt-1 px-3 py-2 border border-[#E2E2DF] rounded text-sm" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-[#E2E2DF]">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button data-testid="ac-edit-save" onClick={save} disabled={saving || !editing.name?.trim()} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50"><Save size={14}/> {saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
